import { Component, inject, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { VehicleTypeService } from '../service/VehicleType.service';
import { VehicleTypeModel } from '../models/VehicleTypeModel';
import { AuthService } from '../service/auth.service';
import { fadeInAnimation } from '../animations/fadeInAnimation';

@Component({
    selector: 'app-vehicle-types-table',
    templateUrl: './vehicle-types-table.component.html',
    styleUrls: ['./vehicle-types-table.component.css'],
    animations: [fadeInAnimation],
    host: { '[@fadeInAnimation]': '' }
})
export class VehicleTypesTableComponent implements OnInit, OnDestroy {

    public vehicleTypeService = inject(VehicleTypeService);
    @ViewChild('tableContainer', { static: false }) tableContainer!: ElementRef;

    authService = inject(AuthService) as AuthService;

    duplicateTypeName = false;
    duplicateTypeValue = '';
    searchQuery = '';
    draft: VehicleTypeModel = {} as VehicleTypeModel;

    get filteredTypes(): VehicleTypeModel[] {
        const query = this.searchQuery.trim().toLowerCase();
        return query ? this.vehicleTypeService.vehicleTypes().filter(type =>
            type.correctVehicleType?.toLowerCase().includes(query) ||
            type.acceptedVehicleType?.toLowerCase().includes(query)
        ) : this.vehicleTypeService.vehicleTypes();
    }

    originalIndex(type: VehicleTypeModel): number {
        return this.vehicleTypeService.vehicleTypes().findIndex(item => item.id === type.id);
    }

    get canSaveNewRow(): boolean {
        const row = this.vehicleTypeService.newInputRow();
        return !!row.correctVehicleType?.trim() && !!row.acceptedVehicleType?.trim() && !this.duplicateTypeName;
    }

    get canSaveDraft(): boolean {
        return !!this.draft.correctVehicleType?.trim() && !!this.draft.acceptedVehicleType?.trim() && !this.duplicateTypeName;
    }

    acceptedNames(names: string | null): string[] {
        return names?.split(',').map(name => name.trim()).filter(Boolean) ?? [];
    }

    ngOnInit() {
        this.getVehicleTypes();
    }

    ngOnDestroy() {
        this.vehicleTypeService.resetComponentState();
    }

    onNewTypeInput(value: string) {
        this.duplicateTypeValue = value;

        if (!value || !value.trim()) {
            this.duplicateTypeName = false;
            return;
        }

        const isDuplicate = this.vehicleTypeService.checkDuplicateType(value);
        this.duplicateTypeName = isDuplicate;
    }

    onEditTypeInput(value: string, currentElement: VehicleTypeModel) {
        this.duplicateTypeValue = value;

        if (!value || !value.trim()) {
            this.duplicateTypeName = false;
            return;
        }

        const otherTypes = this.vehicleTypeService
            .vehicleTypes()
            .filter(v => v.id !== currentElement.id)
            .map(v => v.correctVehicleType?.toLowerCase().trim());

        const isDuplicate = otherTypes.includes(value.toLowerCase().trim());
        this.duplicateTypeName = isDuplicate;
    }


    getVehicleTypes() {
        this.vehicleTypeService.getVehicleTypesTable();
    }

    onAddItems() {
        this.vehicleTypeService.openNewRow();

        setTimeout(() => {
            if (this.tableContainer?.nativeElement) {
                this.tableContainer.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    saveNewRow() {
        this.vehicleTypeService.addVehicleType();
    }

    deleteRow() {
        this.vehicleTypeService.editInputRow.set(false);
        this.duplicateTypeName = false;
        this.duplicateTypeValue = '';
    }

    onEditItems(index: number) {
        this.vehicleTypeService.editInputRow.set(false);
        this.duplicateTypeName = false;
        this.draft = {...this.filteredTypes[index]};
        this.vehicleTypeService.typeIndex.set(this.vehicleTypeService.vehicleTypes().findIndex(type => type.id === this.draft.id));
    }

    onSaveItems() {
        if (!this.canSaveDraft) return;
        this.vehicleTypeService.updateVehicleType(this.draft);
    }

    onDeleteItems(element: VehicleTypeModel) {
        this.vehicleTypeService.deleteVehicleType(element);
    }

    onExitItems() {
        this.vehicleTypeService.typeIndex.set(null);
        this.draft = {} as VehicleTypeModel;
        this.duplicateTypeName = false;
        this.duplicateTypeValue = '';
    }

    onScroll() {
        this.vehicleTypeService.onScroll();
    }

    changeSortDirection(column: string) {
        const currentSort = this.vehicleTypeService.sortDirectionValues();
        let newDirection: 'asc' | 'desc' = 'asc';

        if (currentSort.active === column) {
            newDirection = currentSort.direction === 'asc' ? 'desc' : 'asc';
        }

        this.vehicleTypeService.changeSortDirection(
            { active: column, direction: newDirection },
            this.tableContainer?.nativeElement
        );
    }

    onRefreshTable() {
        this.vehicleTypeService.onRefreshTable();
        this.searchQuery = '';

        this.duplicateTypeName = false;
        this.duplicateTypeValue = '';

        setTimeout(() => {
            if (this.tableContainer?.nativeElement) {
                this.tableContainer.nativeElement.scrollTop = 0;
            }
        }, 0);
    }

    hasPermission() {
        return this.authService.isSuperAdmin();
    }
}
